/**
 * The storefront RESUME CODE -- carried in the URL fragment (`#r=<code>`).
 *
 * Plan: docs/planning/2026-10-02-storefront-session-everywhere-plan.md
 * (Jake, 2026-10-02: "now").
 *
 * WHY. A phone can discard site storage (WebKit, prod 2026-10-01/02), which
 * takes the anonymous visitor's session token -- and with it their cart --
 * along. The server issues a short random code with every anonymous mint,
 * bound to that anonymous account and that store. The page keeps it in the
 * URL FRAGMENT, so a reload of the same URL can trade it back for a fresh
 * token on the SAME account (`POST /auth/resumeStorefrontSession`), and the
 * cart is intact.
 *
 * WHY THE FRAGMENT. A browser never sends the fragment to any server, so the
 * code cannot reach load-balancer logs, Stripe, image hosts or a Referer
 * header. It is never put in a query string, and never sent anywhere but the
 * redeem call.
 *
 * WHAT IT CAN DO. Only ever restore an ANONYMOUS account for ONE store. It can
 * never sign anyone in. A pasted cart URL shares that cart -- accepted.
 *
 * STATE. Memory, mirrored to storage. The URL is the copy that survives the
 * browser losing storage; the storage copy is what puts the code back INTO
 * the URL when the visitor arrives without it (a typed URL, a new tab from a
 * link) while storage is still intact -- otherwise that tab would carry no
 * fragment, and a later storage loss would take the cart with it. The stored
 * copy is bound to the account it restores, and is only used while storage
 * still names that account. Held per store slug, so another store's pages
 * never carry it.
 */
import { storefrontSlugFromPath } from "@/router/storefrontSlug";
import { isAnonymousStorefrontSessionNow } from "@/router/sessionProvenance";

/** The fragment parameter name: `#r=<code>`. */
export const STOREFRONT_RESUME_PARAM = "r";

// Server-issued codes are URL-safe tokens. Anything else in the fragment is
// not ours and is never sent to the server.
const CODE_SHAPE = /^[A-Za-z0-9_-]{8,256}$/;

const STORAGE_KEY = "storefrontResumeCode";

let held = null; // { slug, code, accountId }

function readStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(value) {
  try {
    if (value) localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Unusable storage: memory and the URL carry the code for this page.
  }
}

function readStored() {
  try {
    const value = JSON.parse(readStorage(STORAGE_KEY) || "null");
    return value && typeof value === "object" ? value : null;
  } catch {
    return null;
  }
}

/** The code a fragment (`#r=<code>`, with or without `#`) carries, or null. */
export function resumeCodeFromHash(hash) {
  if (typeof hash !== "string") return null;
  const body = hash.startsWith("#") ? hash.slice(1) : hash;
  const prefix = `${STOREFRONT_RESUME_PARAM}=`;
  if (!body.startsWith(prefix)) return null;
  const code = body.slice(prefix.length);
  return CODE_SHAPE.test(code) ? code : null;
}

/**
 * Remember `code` as the resume code for store `slug`, restoring the
 * anonymous account `accountId`.
 */
export function holdResumeCode(slug, code, accountId) {
  if (!slug || !CODE_SHAPE.test(String(code || ""))) return;
  held = {
    slug: String(slug),
    code: String(code),
    accountId: accountId == null ? null : String(accountId),
  };
  writeStored(held);
}

/**
 * The code held for store `slug`, or null. The stored copy (a new page load)
 * counts only while storage still holds the session it restores.
 */
export function heldResumeCode(slug) {
  if (slug == null) return null;
  if (held) return held.slug === String(slug) ? held.code : null;
  const stored = readStored();
  if (!stored || stored.slug !== String(slug) || !CODE_SHAPE.test(String(stored.code || ""))) {
    return null;
  }
  if (stored.accountId == null || stored.accountId !== readStorage("userId")) return null;
  return stored.code;
}

/** Drop the held code (a refused redeem, a replaced session). */
export function forgetResumeCode() {
  held = null;
  writeStored(null);
}

/** A new page load, for tests: memory is gone, storage is not. */
export function forgetResumeCodeMemoryOnly() {
  held = null;
}

function replaceFragment(fragment) {
  if (typeof window === "undefined" || !window.history) return;
  const { pathname, search } = window.location;
  // replaceState: no history entry, no navigation, no request. The router's
  // own state object is passed back untouched so back/forward keep working.
  window.history.replaceState(window.history.state, "", `${pathname}${search}${fragment}`);
}

/**
 * Put the held code into the address bar, if this page should carry it: a
 * page of the SAME store, while the session is still the anonymous one (a
 * signed-in shopper's URL must not hand out their old anonymous cart), and
 * only when the page has no fragment of its own.
 */
export function writeResumeFragment() {
  if (typeof window === "undefined" || !window.location) return;
  const slug = storefrontSlugFromPath(window.location.pathname);
  const code = heldResumeCode(slug);
  if (!code || !isAnonymousStorefrontSessionNow()) return;
  const hash = window.location.hash || "";
  if (hash && !resumeCodeFromHash(hash) && hash !== "#") return;
  const fragment = `#${STOREFRONT_RESUME_PARAM}=${code}`;
  if (hash !== fragment) replaceFragment(fragment);
}

/** Remove a resume fragment from the address bar (a refused code). */
export function dropResumeFragment() {
  if (typeof window === "undefined" || !window.location) return;
  const hash = window.location.hash || "";
  if (hash.startsWith(`#${STOREFRONT_RESUME_PARAM}=`)) replaceFragment("");
}

/**
 * Keep the fragment across in-app navigation. vue-router builds each new URL
 * from the route, which carries no fragment, so the code would vanish on the
 * first click; this re-applies it after every navigation.
 */
export function installResumeFragment(router) {
  router.afterEach(() => {
    writeResumeFragment();
  });
}
